/**
 * @fileoverview Hook for determining project card display based on kind
 */

import { useMemo } from 'react'
import type { ArcProject } from '../types'

interface ProjectCardInfo {
  /** The value to display (grade names or group name) */
  displayValue: string
  /** The translation key for the label */
  labelKey: string
  /** Display type: grade for templates, group for applications */
  type: 'grade' | 'group'
}

/**
 * Determines what information to display on a project card based on project kind
 * @param project - The project to get display info for
 * @param isLibrary - Whether displaying in library context
 * @returns Display configuration with value, label key, and type
 *
 * @remarks
 * - Templates show grades from projectGrades array
 * - Applications show group name from groupName field
 * - Library context uses "suggested grade" label
 */
export const useProjectCardInfo = (project: ArcProject, isLibrary = false): ProjectCardInfo => {
  return useMemo(() => {
    const isTemplate = project.kind === 'template'

    // Library always shows suggested grade (templates in library don't have groups)
    if (isLibrary || isTemplate) {
      const gradesText = getGradesText(project.projectGrades)
      return {
        displayValue: gradesText || '-',
        labelKey: isLibrary ? 'arc_projects.suggested_grade_label' : 'arc_projects.grade_label',
        type: 'grade',
      }
    }

    return {
      displayValue: project.groupName || '-',
      labelKey: 'arc_projects.group_label',
      type: 'group',
    }
  }, [project.kind, project.projectGrades, project.groupName, isLibrary])
}

/**
 * Extracts and formats grade names from projectGrades array
 * @param projectGrades - Array of project grade associations
 * @returns Comma-separated grade names or null if empty
 */
function getGradesText(
  projectGrades: ArcProject['projectGrades'] | undefined,
): string | null {
  if (!projectGrades || !Array.isArray(projectGrades)) return null
  if (projectGrades.length === 0) return null

  const names = projectGrades
    .filter(pg => pg?.grade?.name)
    .map(pg => pg.grade.name)

  return names.length > 0 ? names.join(', ') : null
}
